import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * ModalScroll — scrollable container with the native OS scrollbar fully hidden,
 * topped by a custom thin rectangle thumb (sharp corners, palette warm-taupe).
 * Guarantees no arrow buttons and no rounded thumb on any browser/OS.
 *
 * Important: the wrapper has `min-h-0` so it can shrink inside a flex column,
 * and `overflow-hidden` so the native scrollbar can't leak through.
 */
export function ModalScroll({ children, className = "", testId }) {
  const viewportRef = useRef(null);
  const [thumb, setThumb] = useState({ top: 0, height: 0, visible: false });

  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const update = () => {
      const { clientHeight, scrollHeight, scrollTop } = el;
      if (scrollHeight <= clientHeight + 1) {
        setThumb((p) => (p.visible ? { top: 0, height: 0, visible: false } : p));
        return;
      }
      const ratio = clientHeight / scrollHeight;
      const thumbH = Math.max(28, Math.round(clientHeight * ratio));
      const trackH = clientHeight - thumbH;
      const top = Math.round((scrollTop / (scrollHeight - clientHeight)) * trackH);
      setThumb({ top, height: thumbH, visible: true });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true, subtree: true, attributes: true, characterData: true });
    const timers = [50, 200, 500, 1000].map((ms) => window.setTimeout(update, ms));
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      ro.disconnect();
      mo.disconnect();
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [children]);

  const dragState = useRef(null);
  useEffect(() => {
    const onMove = (e) => {
      const drag = dragState.current;
      if (!drag) return;
      const el = viewportRef.current;
      if (!el) return;
      const dy = e.clientY - drag.startY;
      const trackH = el.clientHeight - drag.thumbH;
      const newScrollTop = drag.startScrollTop + (dy / trackH) * (el.scrollHeight - el.clientHeight);
      el.scrollTop = Math.max(0, Math.min(newScrollTop, el.scrollHeight - el.clientHeight));
    };
    const onUp = () => { dragState.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  function onThumbMouseDown(e) {
    const el = viewportRef.current;
    if (!el) return;
    e.preventDefault();
    dragState.current = { startY: e.clientY, startScrollTop: el.scrollTop, thumbH: thumb.height };
  }

  return (
    <div
      className={`relative min-h-0 overflow-hidden ${className}`}
      style={{ flex: "1 1 0" }}
      data-testid={testId}
    >
      <div ref={viewportRef} className="h-full overflow-y-auto pr-3 hide-native-scroll">
        {children}
      </div>
      {thumb.visible && (
        <div
          aria-hidden="true"
          onMouseDown={onThumbMouseDown}
          className="absolute right-1 w-1.5 bg-line hover:bg-muted_text transition-colors cursor-pointer"
          style={{ top: thumb.top + "px", height: thumb.height + "px" }}
        />
      )}
    </div>
  );
}
